import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  ArrayInput,
  Button as RAButton,
  FormDataConsumer,
  SimpleFormIterator,
} from "react-admin";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  useTheme
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface Attribute {
  source: string;
  label: string;
}

interface BulkAction {
  label: string;
  /** receives the full array, returns the updated array */
  action: (items: any[]) => any[];
}

interface ArrayEditorProps {
  cardLabel?: string;
  arrayLabel?: string;
  source: string;
  attributes: Attribute[];
  getNewItem?: () => any;
  EditComponent: React.FC<{ item: any; onChange: (updatedItem: any) => void }>; // Updated to use onChange
  bulkActions?: BulkAction[];
}

const ArrayEditor: React.FC<ArrayEditorProps> = ({
  source,
  attributes,
  cardLabel = "",
  arrayLabel = "",
  getNewItem,
  EditComponent,
  bulkActions = [],            // ← default to none
}) => {
  const { getValues, setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const theme = useTheme();

  // New: run a bulk action over the entire array
  const handleBulk = (fn: (items: any[]) => any[]) => {
    const currentArray = getValues(source) || [];
    const updated = fn(currentArray);
    setValue(source, updated, { shouldDirty: true });
  };

  const [editingItem, setEditingItem] = useState<any>(null);  // Separate state for editing
  
  const handleEditClick = (item: any) => {
    setCurrentItem(item);
    setEditingItem({ ...item });  // Clone for editing
    setOpen(true);
  };

  const handleItemChange = (updatedItem: any) => {
    // Store changes in editing state, don't update currentItem until save
    setEditingItem(updatedItem);
  };

  const handleSave = () => {
    const currentArray = getValues(source) || [];
    const updatedArray = currentArray.map((item) =>
      item.id === currentItem.id ? {
        ...editingItem,  // Use editingItem instead of currentItem
        updatedAt: new Date().toISOString(),
      }
        : item
    );
    setValue(source, updatedArray, { shouldDirty: true });
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentItem(null);
    setEditingItem(null);
  };

  const handleAddClick = () => {
    const currentArray = getValues(source) || [];
    const newItem = getNewItem ? getNewItem() : {};
    setValue(source, [...currentArray, newItem], { shouldDirty: true });
  };

  const EditButton: React.FC<{ item: any }> = ({ item }) => (
    <RAButton
      variant="outlined"
      label="Edit"
      color="primary"
      onClick={() => handleEditClick(item)}
    />
  );

  const AddButton: React.FC = () => (
    <RAButton
      variant="outlined"
      color="primary"
      sx={{ backgroundColor: 'white', color: 'black' }}
      startIcon={<AddIcon />}
      label="Add"
      onClick={handleAddClick}
    />
  );

  return (
    <>
      {open && (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogContent dividers>
            <EditComponent item={editingItem} onChange={handleItemChange} />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ width: "100%", mb: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {bulkActions.map(({ label, action }) => (
          <RAButton
            key={label}
            variant="contained"
            color="primary"
            label={label}
            onClick={() => handleBulk(action)}
          />
        ))}
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #cfd8dc",
          boxShadow: "inset 0 4px 8px rgba(0, 0, 0, 0.15)",
          minWidth: "50%",
          margin: "16px 0",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "16px",
          }}
        >
          <ArrayInput source={source} label={arrayLabel}>
            <SimpleFormIterator disableAdd disableClear
              sx={{
                "& .RaSimpleFormIterator-line": {
                  borderBottom: "none",
                },
                "& .RaSimpleFormIterator-form": {
                  width: "100%",
                },
              }}
            >
              <FormDataConsumer>
                {({ scopedFormData }) => (
                  <Box
                    sx={{
                      minWidth: "300px",
                      width: "100%",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      padding: "20px",
                      marginBottom: "16px",
                      backgroundColor: theme.palette.background.default,
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {cardLabel}
                    </Typography>
                    {attributes.map((attr) => (
                      <Typography key={attr.source} variant="body1" gutterBottom>
                        <b>{attr.label}</b>: {scopedFormData?.[attr.source]}
                      </Typography>
                    ))}
                    <EditButton item={scopedFormData} />
                  </Box>
                )}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
          <AddButton />
        </Box>
      </Box>
    </>
  );
};

export default ArrayEditor;
